"use client";

import { useState } from "react";
import { toast } from "@/shared/components/toast";
import { useCartStore } from "@/modules/cart";
import { getActiveStoreId } from "@/modules/store";
import { repeatOrder } from "../services/order.service";
import { describeRepeatResult } from "../lib/repeat-order";
import type { RepeatOrderResult } from "../types/order.dto";

/**
 * "Pedir novamente" / "Refazer última compra": manda os itens de um pedido ao
 * carrinho da sessão (validados contra a loja ativa), atualiza o carrinho,
 * abre a gaveta e avisa o que entrou e o que ficou de fora.
 *
 * Um só fluxo para "meus pedidos" e para o card do header — quem chama só
 * decide o que fazer depois (ex.: fechar o drawer do pedido).
 *
 * Resolve com o resultado do back (mesmo quando nada foi adicionado) ou
 * `null` quando a chamada falhou ou já havia uma em andamento.
 */
export function useRepeatOrder() {
    const [repeating, setRepeating] = useState(false);

    async function repeat(orderId: string | number): Promise<RepeatOrderResult | null> {
        if (repeating) return null;
        setRepeating(true);
        try {
            const result = await repeatOrder(Number(orderId), getActiveStoreId());
            const feedback = describeRepeatResult(result);
            if (feedback.kind === "error") {
                toast.error(feedback.message);
                return result;
            }
            await useCartStore.getState().refresh({ trackChanges: true });
            useCartStore.getState().open();
            if (feedback.kind === "warning") toast.warning(feedback.message);
            else toast.success(feedback.message);
            return result;
        } catch {
            toast.error("Não foi possível repetir o pedido.");
            return null;
        } finally {
            setRepeating(false);
        }
    }

    return { repeat, repeating };
}
